import type { components } from "@countdown/api-client";
import { FileDrop } from "@countdown/door-editor";
import {
  type CalendarGrid,
  type CalendarLayout,
  type CalendarStyle,
  CUSTOMER_ACTION_SUBTITLE,
  DEFAULT_ACTION_SUBTITLE,
  DEFAULT_CALENDAR_STYLE,
  type DoorLook,
  type DoorOpening,
  type EmbedLook,
  FieldGrid,
  type FieldGridEntry,
  type FieldState,
  NUMERAL_FONT_LABELS,
  type NumeralFont,
  type NumeralStyle,
  type PastMarker,
  SCENE_MIN_CONTRAST,
  SCENE_PRESETS,
  SponsoredNotice,
  TenantBrandHeader,
  TenantLegalFooter,
  type TodayHighlight,
  contrastRatio,
  parseSceneInput,
  presentFieldState,
  sceneBackground,
  sceneFooterContrast,
  sceneStyle,
} from "@countdown/theme";
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";

import { CropButton, assetIdOf } from "@/components/CropButton";
import { Notice } from "@/components/Notice";
import { ParticipantFrame } from "@/components/ParticipantFrame";
import { ScaledDevice } from "@/components/PreviewLayout";
import { SaveForm } from "@/components/SaveForm";
import { Advanced } from "@/components/Section";
import { UnscannedAccept } from "@/components/UnscannedAccept";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type ActionDetail,
  type ActionOutcome,
  mediaSrc,
  saveActionSection,
  uploadPlaceholderImage,
} from "@/lib/api";
import {
  type CalendarDraft,
  draftOf,
  layoutRequest,
  numberRequest,
  previewArrangement,
  sameValue,
  sampleState,
  styleRequest,
} from "@/lib/calendar-draft";

/**
 * The calendar's design per action (D29): arrangement and grid, the door number, the picture
 * of the unbooked doors; and the look of the doors (D31). Every preview here is the calendar's
 * own drawing: the shared
 * `FieldGrid` with the shared stylesheet, fed with the values on the screen before they are
 * saved, so what the form shows is what the calendar will show.
 */

/** A picture behind the open doors of a preview when there is no real one. */
export interface PreviewLogo {
  readonly src: string;
  readonly alt: string;
}

export const SAMPLE_LOGO: PreviewLogo = {
  src:
    "data:image/svg+xml," +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 60"><rect width="120" height="60" fill="#d9e1ea"/><text x="60" y="38" font-family="arial" font-size="20" text-anchor="middle" fill="#646c79">Logo</text></svg>',
    ),
  alt: "Beispiellogo",
};

/** One fixed day, so the previews show the same states on every visit. */
const SAMPLE_TIMING = {
  unlocksAt: "2026-12-06T23:00:00.000Z",
  participationEndsAt: "2026-12-07T22:59:59.000Z",
  followUpEndsAt: "2027-01-07T22:59:59.000Z",
};

export function present(state: FieldState) {
  return presentFieldState({ state, timing: SAMPLE_TIMING, timeZone: "Europe/Berlin" });
}

const LAYOUTS: readonly (readonly [CalendarLayout, string])[] = [
  ["mosaic", "Mosaik"],
  ["grid", "Raster"],
  ["honeycomb", "Waben"],
];

const DEVICES = [
  ["mobile", "Handy", 375],
  ["tablet", "Tablet", 700],
  ["desktop", "Desktop", 960],
] as const;

/** The width of the article column the calendar sits in on the magazine websites (D36). */
const ARTICLE_WIDTH = 984;
type Device = (typeof DEVICES)[number][0];

const RATIOS: readonly (readonly [number, number, string])[] = [
  [1, 1, "Quadrat 1:1"],
  [4, 3, "Quer 4:3"],
  [3, 2, "Quer 3:2"],
  [3, 4, "Hoch 3:4"],
  [2, 3, "Hoch 2:3"],
];

const SELECT_CLASS = "h-9 border border-input bg-background px-2 text-sm";

// ------------------------------------------------------- elements of the click editor (D49)

/** What a click in the design preview chooses; each element has its own settings (D49). */
export type DesignElement =
  | "anordnung"
  | "heute"
  | "geschlossen"
  | "vergangen"
  | "zahl"
  | "bild"
  | "verzierungen"
  | "hintergrund"
  | "veranstalter"
  | "website";

/** The elements in the order of the bar above their settings. */
export const DESIGN_ELEMENTS: readonly (readonly [DesignElement, string])[] = [
  ["anordnung", "Anordnung"],
  ["heute", "Heutiges Türchen"],
  ["geschlossen", "Geschlossene Türchen"],
  ["vergangen", "Vergangene Türchen"],
  ["zahl", "Türchenzahl"],
  ["bild", "Bild für unbelegte Türchen"],
  ["verzierungen", "Verzierungen"],
  ["hintergrund", "Hintergrund"],
  ["veranstalter", "Veranstalter"],
  ["website", "Auf Websites"],
];

/** The elements of a draft: a honeycomb has no door shapes, so its closed doors are no element. */
export function elementsFor(
  draft: Pick<CalendarDraft, "layout">,
): readonly (readonly [DesignElement, string])[] {
  return DESIGN_ELEMENTS.filter(
    ([element]) => element !== "geschlossen" || draft.layout !== "honeycomb",
  );
}

/**
 * The element a click in the preview lands on, the innermost first; `null` chooses nothing (the
 * card's texts, a header with the brand's logo). The footer, and the header that names the
 * organizer for want of a logo, choose the organizer (D50). Outside the card the click is on
 * the night.
 */
export function elementAt(
  target: EventTarget | null,
  { honeycomb, hasLogo }: { readonly honeycomb: boolean; readonly hasLogo: boolean },
): DesignElement | null {
  // The target comes from the frame's window, where `instanceof Element` of this one is false.
  if (target === null || !("closest" in target) || typeof target.closest !== "function") {
    return null;
  }
  const element = target as Element;
  const hit = (selector: string): boolean => element.closest(selector) !== null;
  if (hit(".countdown-field__door-number, .countdown-field__numeral")) {
    return "zahl";
  }
  if (hit('[data-field-state="today"]')) {
    return "heute";
  }
  if (hit('[data-field-state="locked"]')) {
    return honeycomb ? "anordnung" : "geschlossen";
  }
  if (hit('[data-field-state^="past"]')) {
    return "vergangen";
  }
  if (hit(".countdown-field--empty")) {
    return "verzierungen";
  }
  if (hit(".countdown-field-grid-frame")) {
    return "anordnung";
  }
  if (hit('[data-design-target="fuss"]') || (!hasLogo && hit('[data-design-target="kopf"]'))) {
    return "veranstalter";
  }
  if (hit("[data-design-target], .countdown-card")) {
    return null;
  }
  return "hintergrund";
}

/** The props of one element's part of a form: shown only while that element is chosen. */
export function part(chosen: DesignElement, of: DesignElement) {
  return { hidden: chosen !== of, "data-design-panel": of } as const;
}

// ------------------------------------------------------------ arrangement and grid

/**
 * Whether a calendar form differs from what is saved, and its save. The stored state follows
 * only once the page has reloaded the action, so until then the request just sent counts as
 * saved too. Measured against the stored state alone, a form stayed unsaved after "Gespeichert.":
 * a tab change in that moment asked to leave, and a second "Speichern" wrote the same state again.
 */
export function useSentRequest<T>(
  request: T,
  stored: T,
  save: (request: T) => Promise<ActionOutcome>,
): { readonly dirty: boolean; readonly submit: () => Promise<ActionOutcome> } {
  const [sent, setSent] = useState<{ readonly request: T } | null>(null);
  return {
    dirty: !sameValue(request, stored) && (sent === null || !sameValue(request, sent.request)),
    submit: async () => {
      const outcome = await save(request);
      if (outcome.ok) {
        setSent({ request });
      }
      return outcome;
    },
  };
}

/** What the three calendar forms share: the action, the page's draft and how to change it. */
interface DraftFormProps {
  readonly detail: ActionDetail;
  readonly draft: CalendarDraft;
  readonly onChange: Dispatch<SetStateAction<CalendarDraft>>;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
  /** The element whose settings show; the others stay mounted for the save bar (D49). */
  readonly chosen: DesignElement;
}

/** Arrangement, columns per screen size and the ratio of a door: the element "Anordnung". */
export function CalendarLayoutForm({
  detail,
  draft,
  onChange,
  readOnly,
  onSaved,
  chosen,
}: DraftFormProps): ReactElement {
  const id = useId();
  const { layout, custom, grid } = draft;
  const presetRatio = RATIOS.some(
    ([width, height]) => width === grid.tile_ratio.width && height === grid.tile_ratio.height,
  );

  const setLayout = (next: CalendarLayout): void => {
    onChange((current) => ({ ...current, layout: next }));
  };
  const setCustom = (next: boolean): void => {
    onChange((current) => ({ ...current, custom: next }));
  };
  const setColumns = (key: keyof CalendarGrid["columns"], value: number): void => {
    onChange((current) => ({
      ...current,
      grid: { ...current.grid, columns: { ...current.grid.columns, [key]: value } },
    }));
  };
  const setRatio = (width: number, height: number): void => {
    onChange((current) => ({ ...current, grid: { ...current.grid, tile_ratio: { width, height } } }));
  };
  const saving = useSentRequest(layoutRequest(draft), layoutRequest(draftOf(detail)), (body) =>
    saveActionSection("calendar-layout", body, detail.action_id),
  );

  return (
    <SaveForm
      label="Anordnung und Raster"
      readOnly={readOnly}
      onSaved={onSaved}
      dirty={saving.dirty}
      submit={saving.submit}
      hidden={chosen !== "anordnung"}
    >
      <div {...part(chosen, "anordnung")}>
        <fieldset>
          <legend className="sr-only">Anordnung</legend>
          {LAYOUTS.map(([value, label]) => (
            <div className="mt-2 flex items-center gap-2 first-of-type:mt-0" key={value}>
              <input
                className="border border-input"
                id={`${id}-layout-${value}`}
                name="calendar_layout"
                type="radio"
                checked={layout === value}
                onChange={() => {
                  setLayout(value);
                }}
              />
              <label className="text-sm" htmlFor={`${id}-layout-${value}`}>
                {label}
              </label>
            </div>
          ))}
        </fieldset>

        <div className="mt-5 flex items-center gap-2">
          <input
            id={`${id}-custom`}
            type="checkbox"
            className="border border-input"
            checked={custom}
            onChange={(event) => {
              setCustom(event.target.checked);
            }}
          />
          <Label htmlFor={`${id}-custom`}>Spalten und Kachelform selbst festlegen</Label>
        </div>

        {custom ? (
          <>
            <fieldset className="mt-3">
              <legend className="text-sm font-medium">
                {layout === "honeycomb" ? "Waben je Reihe" : "Spalten"}
              </legend>
              <div className="mt-2 grid grid-cols-3 gap-3">
                {DEVICES.map(([key, label]) => (
                  <div className="grid gap-1.5" key={key}>
                    <Label htmlFor={`${id}-columns-${key}`}>{label}</Label>
                    <Input
                      id={`${id}-columns-${key}`}
                      type="number"
                      min={1}
                      max={12}
                      required
                      value={grid.columns[key]}
                      onChange={(event) => {
                        setColumns(key, Number(event.target.value));
                      }}
                    />
                  </div>
                ))}
              </div>
            </fieldset>

            {/* Honeycombs are even hexagons; the tile's form is for the mosaic and the grid. */}
            {layout === "honeycomb" ? null : (
              <fieldset className="mt-4">
                <legend className="text-sm font-medium">Kachelform (Breite zu Höhe)</legend>
                <div className="mt-2 flex flex-wrap gap-2">
                  {RATIOS.map(([width, height, label]) => (
                    <Button
                      key={label}
                      type="button"
                      size="sm"
                      variant={
                        grid.tile_ratio.width === width && grid.tile_ratio.height === height
                          ? "default"
                          : "outline"
                      }
                      onClick={() => {
                        setRatio(width, height);
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
                <div className="mt-3 flex items-end gap-2">
                  <div className="grid gap-1.5">
                    <Label htmlFor={`${id}-ratio-width`}>Eigene Breite</Label>
                    <Input
                      className="w-20"
                      id={`${id}-ratio-width`}
                      type="number"
                      min={1}
                      max={4}
                      value={grid.tile_ratio.width}
                      onChange={(event) => {
                        setRatio(Number(event.target.value), grid.tile_ratio.height);
                      }}
                    />
                  </div>
                  <span className="pb-2">:</span>
                  <div className="grid gap-1.5">
                    <Label htmlFor={`${id}-ratio-height`}>Höhe</Label>
                    <Input
                      className="w-20"
                      id={`${id}-ratio-height`}
                      type="number"
                      min={1}
                      max={4}
                      value={grid.tile_ratio.height}
                      onChange={(event) => {
                        setRatio(grid.tile_ratio.width, Number(event.target.value));
                      }}
                    />
                  </div>
                  {presetRatio ? null : (
                    <span className="pb-2 text-sm text-muted-foreground">eigenes Format</span>
                  )}
                </div>
              </fieldset>
            )}
          </>
        ) : null}
      </div>
    </SaveForm>
  );
}

// ------------------------------------------------------------------ door number

/** Nine places to start from; the sliders fine-tune them. */
const ANCHORS: readonly (readonly [number, number, string])[] = [
  [18, 18, "oben links"],
  [50, 18, "oben Mitte"],
  [82, 18, "oben rechts"],
  [18, 50, "Mitte links"],
  [50, 50, "Mitte"],
  [82, 50, "Mitte rechts"],
  [18, 82, "unten links"],
  [50, 82, "unten Mitte"],
  [82, 82, "unten rechts"],
];

/** The grounds a door number sits on: the door of a square and of a hexagon. */
const DOOR_GROUNDS = ["#ffffff", "#f0f4f8"] as const;

function formatRatio(ratio: number): string {
  return ratio.toLocaleString("de-DE", { maximumFractionDigits: 1 });
}

export function NumeralControls({
  idPrefix,
  title,
  value,
  onChange,
  checkContrast,
}: {
  readonly idPrefix: string;
  readonly title: string;
  readonly value: NumeralStyle;
  readonly onChange: (next: NumeralStyle) => void;
  readonly checkContrast: boolean;
}): ReactElement {
  const set = <K extends keyof NumeralStyle>(key: K, next: NumeralStyle[K]): void => {
    onChange({ ...value, [key]: next });
  };
  const ratios = DOOR_GROUNDS.map((ground) => contrastRatio(value.color, ground) ?? 21);
  const weakest = Math.min(...ratios);

  return (
    <fieldset className="grid content-start gap-3 border border-border p-3">
      <legend className="px-1 text-sm font-medium">{title}</legend>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-font`}>Schriftart</Label>
        <select
          className={SELECT_CLASS}
          id={`${idPrefix}-font`}
          value={value.font}
          onChange={(event) => {
            set("font", event.target.value as NumeralFont);
          }}
        >
          {Object.entries(NUMERAL_FONT_LABELS).map(([font, label]) => (
            <option key={font} value={font}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-weight`}>Stärke</Label>
        <select
          className={SELECT_CLASS}
          id={`${idPrefix}-weight`}
          value={value.weight}
          onChange={(event) => {
            set("weight", event.target.value === "regular" ? "regular" : "bold");
          }}
        >
          <option value="bold">fett</option>
          <option value="regular">normal</option>
        </select>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-layer`}>Ebene</Label>
        <select
          className={SELECT_CLASS}
          id={`${idPrefix}-layer`}
          value={value.layer}
          onChange={(event) => {
            set("layer", event.target.value === "behind" ? "behind" : "front");
          }}
        >
          <option value="front">vor dem Logo</option>
          <option value="behind">hinter dem Logo</option>
        </select>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-color`}>Schriftfarbe</Label>
        <div className="flex items-center gap-2">
          <input
            className="h-9 w-12 border border-input bg-background"
            id={`${idPrefix}-color`}
            type="color"
            value={value.color}
            onChange={(event) => {
              set("color", event.target.value);
            }}
          />
          <span className="font-mono text-sm">{value.color}</span>
        </div>
        {checkContrast && weakest < 3 ? (
          <p className="text-sm text-destructive" role="status">
            Die Zahl hebt sich kaum von der Tür ab (Kontrast {formatRatio(weakest)}:1, mindestens
            3:1 empfohlen).
          </p>
        ) : null}
      </div>
      {(
        [
          ["size", "Größe (% der Kachelbreite)", 5, 80],
          ["x", "Position waagerecht (%)", 0, 100],
          ["y", "Position senkrecht (%)", 0, 100],
          ["rotation", "Neigung (Grad)", -20, 20],
        ] as const
      ).map(([key, label, min, max]) => (
        <div className="grid gap-1.5" key={key}>
          <Label htmlFor={`${idPrefix}-${key}`}>
            {label}: {value[key]}
          </Label>
          <input
            id={`${idPrefix}-${key}`}
            type="range"
            min={min}
            max={max}
            value={value[key]}
            onChange={(event) => {
              set(key, Number(event.target.value));
            }}
          />
        </div>
      ))}
      <div>
        <p className="text-sm">Schnellwahl Position</p>
        <div className="mt-1 grid w-max grid-cols-3 gap-1" role="group" aria-label="Schnellwahl Position">
          {ANCHORS.map(([x, y, label]) => (
            <button
              key={label}
              type="button"
              aria-label={label}
              aria-pressed={value.x === x && value.y === y}
              className={`size-6 border border-border ${
                value.x === x && value.y === y ? "bg-primary" : "bg-background hover:bg-muted"
              }`}
              onClick={() => {
                onChange({ ...value, x, y });
              }}
            />
          ))}
        </div>
      </div>
    </fieldset>
  );
}


/** The door number of the whole action, in the element "Türchenzahl". Off: the built-in look. */
export function DoorNumberForm({
  detail,
  draft,
  onChange,
  readOnly,
  onSaved,
  chosen,
}: DraftFormProps): ReactElement {
  const id = useId();
  const saving = useSentRequest(numberRequest(draft), numberRequest(draftOf(detail)), (body) =>
    saveActionSection("door-number-style", body, detail.action_id),
  );

  return (
    <SaveForm
      label="Türchenzahl"
      readOnly={readOnly}
      onSaved={onSaved}
      dirty={saving.dirty}
      submit={saving.submit}
      hidden={chosen !== "zahl"}
    >
      <div {...part(chosen, "zahl")}>
        <div className="flex items-center gap-2">
          <input
            id={`${id}-custom`}
            type="checkbox"
            className="border border-input"
            checked={draft.numberCustom}
            onChange={(event) => {
              const next = event.target.checked;
              onChange((current) => ({ ...current, numberCustom: next }));
            }}
          />
          <Label htmlFor={`${id}-custom`}>Eigene Darstellung</Label>
        </div>
        {draft.numberCustom ? (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <NumeralControls
              idPrefix={`${id}-door`}
              title="Auf der Tür"
              value={draft.number.door}
              onChange={(door) => {
                onChange((current) => ({ ...current, number: { ...current.number, door } }));
              }}
              checkContrast
            />
            <NumeralControls
              idPrefix={`${id}-open`}
              title="Im offenen Türchen"
              value={draft.number.open}
              onChange={(open) => {
                onChange((current) => ({ ...current, number: { ...current.number, open } }));
              }}
              checkContrast={false}
            />
          </div>
        ) : null}
        {/* The whole element: the number's own look and the tilt above it in the style form. */}
        {readOnly ||
        (!draft.numberCustom &&
          draft.style.number_wobble === DEFAULT_CALENDAR_STYLE.number_wobble) ? null : (
          <Button
            className="mt-4"
            type="button"
            variant="outline"
            onClick={(event) => {
              focusPanelOf(event.currentTarget);
              onChange((current) => ({
                ...current,
                numberCustom: false,
                style: { ...current.style, number_wobble: DEFAULT_CALENDAR_STYLE.number_wobble },
              }));
            }}
          >
            Standard wiederherstellen
          </Button>
        )}
      </div>
    </SaveForm>
  );
}

type ContentImage = components["schemas"]["ContentImageView"];

/** Height of the brand logo in the header; the participant page uses the same. */
const BRAND_LOGO_HEIGHT = "2.75rem";

/**
 * The calendar page as a participant gets it, drawn from the unsaved draft in the participant
 * stylesheet (D49): the seasonal world, the header with the brand's logo, title and subtitle, the
 * doors, the advertising notice and the legal footer. Door 1 is a day gone, door 2 a day taken
 * part in, door 3 today, the rest are to come. The device keeps its width and is scaled to the
 * column, so the frame picks the device's columns.
 *
 * Links and controls of the page are `inert`: a click in the preview never leaves the backoffice.
 * `ponytail:` the seasonal world is the Advent one, as the participant page's fallback theme
 * (cell-context.ts); it follows the action's theme once that is data.
 */
export function DesignPreview({
  detail,
  draft,
  logo,
  brandLogo,
  chosen,
  look,
  onChoose,
}: {
  readonly detail: ActionDetail;
  readonly draft: CalendarDraft;
  readonly logo: PreviewLogo | null;
  /** The brand's logo for the header; without one the header names the organizer or brand. */
  readonly brandLogo: ContentImage | null;
  /** The element whose settings show, outlined in the preview. */
  readonly chosen: DesignElement;
  /** The calendar in a website's frame (D39); drawn while "Auf Websites" is chosen. */
  readonly look: EmbedLook;
  /** A click in the preview chooses the element under it (`elementAt`). */
  readonly onChoose: (element: DesignElement) => void;
}): ReactElement {
  const [device, setDevice] = useState<Device>("mobile");
  const shown = logo ?? SAMPLE_LOGO;
  const fields: FieldGridEntry[] = previewArrangement(detail, draft).map(({ position, size }) =>
    position < 0
      ? { filler: -position - 1, size }
      : { position, size, presentation: present(sampleState(position)), logo: shown },
  );
  // In a website's frame the page has what the action's look keeps, as BaseLayout.astro and
  // Calendar.astro draw it there; the desktop is the magazine's article column.
  const framed = chosen === "website" ? look : null;
  const width =
    framed !== null && device === "desktop"
      ? ARTICLE_WIDTH
      : (DEVICES.find(([key]) => key === device)?.[2] ?? 375);
  // As the participant page names the organizer (cell-context.ts, BaseLayout.astro, D50).
  const operatorName = draft.style.organizer_shown ? (detail.organizer_name ?? "") : "";
  const calendarStyle =
    framed === null || framed.decor
      ? draft.style
      : { ...draft.style, decorations: false, filler_ornaments: false };
  const body = {
    seasonalWorld: framed === null || framed.ground === "scene" ? "christmas" : "none",
    embed: framed === null ? undefined : "",
    embedGround: framed?.ground,
    embedDecor: framed !== null && !framed.decor ? "off" : undefined,
  };

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Bildschirmgröße der Vorschau">
        {DEVICES.map(([key, label]) => (
          <Button
            key={key}
            type="button"
            size="sm"
            variant={device === key ? "default" : "outline"}
            aria-pressed={device === key}
            onClick={() => {
              setDevice(key);
            }}
          >
            {label}
          </Button>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">
        Vorschau: Klicken Sie auf ein Element, um es zu wählen.
      </p>
      <ScaledDevice width={width}>
        <ParticipantFrame
          title="Vorschau des Kalenders"
          page={{ data: body, style: sceneStyle(draft.style) }}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the mouse's way to an element; the keyboard chooses it in the bar above the settings */}
          <div
            data-design-root=""
            data-chosen={chosen}
            onClick={(event) => {
              const element = elementAt(event.target, {
                honeycomb: draft.layout === "honeycomb",
                hasLogo: brandLogo !== null,
              });
              if (element !== null) {
                onChoose(element);
              }
            }}
          >
            <div className="countdown-seasonal" aria-hidden="true">
              <span className="countdown-seasonal__sky" />
              <span className="countdown-seasonal__sleigh" />
              <span className="countdown-seasonal__snow countdown-seasonal__snow--far" />
              <span className="countdown-seasonal__snow countdown-seasonal__snow--mid" />
              <span className="countdown-seasonal__snow countdown-seasonal__snow--near" />
            </div>
            <div className="countdown-shell countdown-shell--wide">
              <div className="countdown-card">
                {framed !== null && !framed.header ? null : (
                  <div data-design-target="kopf">
                    <div inert>
                      <TenantBrandHeader
                        logo={
                          brandLogo === null
                            ? null
                            : {
                                src: mediaSrc(brandLogo.path),
                                alt: brandLogo.alt_text,
                                placement: "left",
                                maxHeight: BRAND_LOGO_HEIGHT,
                              }
                        }
                        name={
                          draft.style.header_name_shown ? operatorName || detail.brand_name : ""
                        }
                        homeHref="#"
                      />
                    </div>
                  </div>
                )}
                <div className="countdown-main">
                  {/* Not an h1: the backoffice page has its own, and this is a picture of a page. */}
                  {framed !== null && !framed.title ? null : (
                    <>
                      <p className="countdown-heading">{detail.title}</p>
                      <p className="countdown-subheading">
                        {(detail.customer_sponsor_id ?? null) !== null
                          ? CUSTOMER_ACTION_SUBTITLE
                          : DEFAULT_ACTION_SUBTITLE}
                      </p>
                    </>
                  )}
                  <FieldGrid
                    fields={fields}
                    label="Vorschau des Kalenders"
                    fieldTerm="door"
                    layout={draft.layout}
                    grid={draft.custom ? draft.grid : null}
                    numberStyle={draft.numberCustom ? draft.number : null}
                    calendarStyle={calendarStyle}
                  />
                  <SponsoredNotice
                    fieldTerm="door"
                    customerCalendar={(detail.customer_sponsor_id ?? null) !== null}
                  />
                </div>
              </div>
              {framed !== null && !framed.footer ? null : (
                <div data-design-target="fuss">
                  <div inert>
                    <TenantLegalFooter
                      legal={{ operatorName, imprintUrl: "#", privacyUrl: "#", termsUrl: "#" }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </ParticipantFrame>
      </ScaledDevice>
    </div>
  );
}

// --------------------------------------------------------------- the look (D31)

type Choice<T> = readonly (readonly [T, string])[];

const DOOR_OPENINGS: Choice<DoorOpening> = [
  ["outward", "Nach außen"],
  ["ajar", "Nach außen, angelehnt"],
  ["double", "Flügeltür"],
  ["inward", "Nach innen"],
  ["none", "Ohne Tür"],
];

const DOOR_LOOKS: Choice<DoorLook> = [
  ["perforated", "Gestanzte Kontur"],
  ["panel", "Tafel mit Knauf"],
  ["plain", "Einfacher Rahmen"],
];

const TODAY_HIGHLIGHTS: Choice<TodayHighlight> = [
  ["lights", "Lichterkette"],
  ["glow", "Leuchten"],
  ["none", "Keine"],
];

const PAST_MARKERS: Choice<PastMarker> = [
  ["stripe", "Markenstrich"],
  ["muted", "Strich und blasses Logo"],
  ["corner", "Eselsohr"],
  ["none", "Ohne Markierung"],
];

/**
 * The style keys each element's "Standard wiederherstellen" sets back. The door number resets
 * with its own form (`DoorNumberForm`), which holds the rest of the element.
 */
const STYLE_KEYS: Partial<Record<DesignElement, readonly (keyof CalendarStyle)[]>> = {
  heute: ["door_opening", "today_highlight"],
  geschlossen: ["door_look"],
  vergangen: ["past_marker"],
  verzierungen: ["filler_count", "filler_ornaments", "decorations"],
  hintergrund: ["scene_color"],
  veranstalter: ["organizer_shown", "header_name_shown"],
};

/** The elements the look of the doors serves. */
const STYLE_ELEMENTS: readonly DesignElement[] = [
  "heute",
  "geschlossen",
  "vergangen",
  "zahl",
  "verzierungen",
  "hintergrund",
  "veranstalter",
];

/**
 * After "Standard wiederherstellen" the button goes (nothing differs any more), so the focus
 * moves to the first control of the element instead of falling to the page.
 */
function focusPanelOf(button: HTMLElement): void {
  const controls = button
    .closest("[data-design-panel]")
    ?.querySelectorAll<HTMLElement>("input, select, button");
  // Not the reset itself (it goes), and nothing folded away in a closed `details`.
  [...(controls ?? [])]
    .find((control) => control !== button && control.closest("details:not([open])") === null)
    ?.focus();
}

/** One setting as a group of radio buttons; the preview shows what each does (D49). */
function ChoiceGroup<T extends string>({
  name,
  legend,
  choices,
  value,
  onChange,
}: {
  readonly name: string;
  readonly legend: string;
  readonly choices: Choice<T>;
  readonly value: T;
  readonly onChange: (next: T) => void;
}): ReactElement {
  const id = useId();
  return (
    <fieldset className="mt-4 first:mt-0">
      <legend className="text-sm font-medium">{legend}</legend>
      {choices.map(([choice, label]) => (
        <div className="mt-2 flex items-center gap-2" key={choice}>
          <input
            className="border border-input"
            id={`${id}-${choice}`}
            name={name}
            type="radio"
            checked={value === choice}
            onChange={() => {
              onChange(choice);
            }}
          />
          <label className="text-sm" htmlFor={`${id}-${choice}`}>
            {label}
          </label>
        </div>
      ))}
    </fieldset>
  );
}

/**
 * The look of the action's doors (D31): how today's door opens, what a closed door looks
 * like, how today and the days gone are marked, the colour of the night, the empty places and
 * their shapes, the decoration and the tilt of the numbers. `DesignPreview` draws it.
 */
export function CalendarStyleForm({
  detail,
  draft,
  onChange,
  readOnly,
  onSaved,
  chosen,
  hasLogo,
}: DraftFormProps & {
  /** The brand has a logo for the header, which then names nobody. */
  readonly hasLogo: boolean;
}): ReactElement {
  const id = useId();
  const { style } = draft;
  const honeycomb = draft.layout === "honeycomb";
  const set = <K extends keyof CalendarStyle>(key: K, next: CalendarStyle[K]): void => {
    onChange((current) => ({ ...current, style: { ...current.style, [key]: next } }));
  };
  const scene = style.scene_color ?? null;
  // The code as typed: a half-typed code stays on the screen while the draft keeps the last
  // colour. A preset, the picker and "Standard wiederherstellen" write the text themselves, also
  // when the colour stays the same; the check below only follows a change from outside (a save).
  const [sceneText, setSceneText] = useState(scene ?? "");
  const [sceneShown, setSceneShown] = useState(scene);
  const setScene = (next: string | null): void => {
    set("scene_color", next);
    setSceneText(next ?? "");
  };
  if (sceneShown !== scene) {
    setSceneShown(scene);
    if (parseSceneInput(sceneText) !== scene) {
      setSceneText(scene ?? "");
    }
  }
  const sceneRatio = scene === null ? null : sceneFooterContrast(scene);
  const sceneTooLight = sceneRatio !== null && sceneRatio < SCENE_MIN_CONTRAST;
  const sceneTextInvalid = parseSceneInput(sceneText) === undefined;
  // Refused through the form's own validity, so the save bar stops at this field.
  const sceneInput = useRef<HTMLInputElement>(null);
  let sceneProblem = "";
  if (sceneTextInvalid) {
    sceneProblem = "Bitte den Farbcode als #rrggbb angeben, zum Beispiel #0f213e.";
  } else if (sceneTooLight) {
    sceneProblem = "Diese Farbe ist für die Schrift am Seitenfuß zu hell.";
  }
  useEffect(() => {
    sceneInput.current?.setCustomValidity(sceneProblem);
  }, [sceneProblem]);
  const saving = useSentRequest(styleRequest(draft), styleRequest(draftOf(detail)), (body) =>
    saveActionSection("calendar-style", body, detail.action_id),
  );

  /** "Standard wiederherstellen" of one element: only its keys, and only while one differs. */
  const reset = (element: DesignElement): ReactElement | null => {
    // Only what the element shows: a honeycomb's door opening is not on the screen.
    const keys = (STYLE_KEYS[element] ?? []).filter(
      (key) => !(honeycomb && key === "door_opening"),
    );
    if (readOnly || keys.every((key) => sameValue(style[key], DEFAULT_CALENDAR_STYLE[key]))) {
      return null;
    }
    return (
      <Button
        className="mt-4"
        type="button"
        variant="outline"
        onClick={(event) => {
          focusPanelOf(event.currentTarget);
          const defaults = Object.fromEntries(
            keys.map((key) => [key, DEFAULT_CALENDAR_STYLE[key]]),
          ) as Partial<CalendarStyle>;
          onChange((current) => ({ ...current, style: { ...current.style, ...defaults } }));
          if (keys.includes("scene_color")) {
            setSceneText("");
          }
        }}
      >
        Standard wiederherstellen
      </Button>
    );
  };

  return (
    <SaveForm
      label="Gestaltung der Türchen"
      readOnly={readOnly}
      onSaved={onSaved}
      // A half-typed code counts as a change, so "Speichern" stops at it instead of passing it by.
      dirty={sceneTextInvalid || saving.dirty}
      submit={saving.submit}
      hidden={!STYLE_ELEMENTS.includes(chosen)}
    >
      <div {...part(chosen, "heute")}>
        {/* A honeycomb's door always opens inward; the door forms are for square doors. */}
        {honeycomb ? null : (
          <ChoiceGroup
            name="door_opening"
            legend="Tür"
            choices={DOOR_OPENINGS}
            value={style.door_opening}
            onChange={(next) => {
              set("door_opening", next);
            }}
          />
        )}
        <ChoiceGroup
          name="today_highlight"
          legend="Hervorhebung"
          choices={TODAY_HIGHLIGHTS}
          value={style.today_highlight}
          onChange={(next) => {
            set("today_highlight", next);
          }}
        />
        {reset("heute")}
      </div>

      <div {...part(chosen, "geschlossen")}>
        {honeycomb ? null : (
          <ChoiceGroup
            name="door_look"
            legend="Türform"
            choices={DOOR_LOOKS}
            value={style.door_look}
            onChange={(next) => {
              set("door_look", next);
            }}
          />
        )}
        {reset("geschlossen")}
      </div>

      <div {...part(chosen, "vergangen")}>
        <ChoiceGroup
          name="past_marker"
          legend="Markierung"
          choices={PAST_MARKERS}
          value={style.past_marker}
          onChange={(next) => {
            set("past_marker", next);
          }}
        />
        {reset("vergangen")}
      </div>

      <div {...part(chosen, "zahl")}>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-wobble`}>
            Neigung der Zahlen: bis {style.number_wobble} Grad
          </Label>
          <input
            id={`${id}-wobble`}
            type="range"
            min={0}
            max={10}
            value={style.number_wobble}
            onChange={(event) => {
              set("number_wobble", Number(event.target.value));
            }}
          />
        </div>
      </div>

      <div {...part(chosen, "verzierungen")}>
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-fillers`}>Lückenfüller: {style.filler_count}</Label>
          <input
            id={`${id}-fillers`}
            type="range"
            min={0}
            max={12}
            value={style.filler_count}
            onChange={(event) => {
              set("filler_count", Number(event.target.value));
            }}
          />
        </div>
        {(
          [
            ["filler_ornaments", "Weihnachtsformen in den Lückenfüllern"],
            ["decorations", "Formen an den Türchen-Ecken"],
          ] as const
        ).map(([key, label]) => (
          <div className="mt-3 flex items-center gap-2" key={key}>
            <input
              id={`${id}-${key}`}
              type="checkbox"
              className="border border-input"
              checked={style[key]}
              onChange={(event) => {
                set(key, event.target.checked);
              }}
            />
            <Label htmlFor={`${id}-${key}`}>{label}</Label>
          </div>
        ))}
        {reset("verzierungen")}
      </div>

      <div {...part(chosen, "hintergrund")}>
        <fieldset>
          <legend className="text-sm font-medium">Farbe der Nacht</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {SCENE_PRESETS.map(([color, label]) => (
              <Button
                key={label}
                type="button"
                size="sm"
                variant={scene === color ? "default" : "outline"}
                aria-pressed={scene === color}
                onClick={() => {
                  setScene(color);
                }}
              >
                <span
                  aria-hidden="true"
                  className="inline-block size-4 border border-border"
                  style={{ background: sceneBackground(color ?? "var(--countdown-color-brand)") }}
                />
                {label}
              </Button>
            ))}
          </div>
          <Advanced
            summary="Eigene Farbe"
            // Open where the night is a colour of its own, or a typed code still needs fixing.
            open={
              (scene !== null && !SCENE_PRESETS.some(([color]) => color === scene)) ||
              sceneTextInvalid
            }
          >
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <input
                className="h-9 w-12 border border-input bg-background"
                id={`${id}-scene`}
                type="color"
                value={scene ?? "#2c1525"}
                onChange={(event) => {
                  setScene(event.target.value);
                }}
              />
              <Label htmlFor={`${id}-scene`}>Eigene Farbe</Label>
              <span className="flex items-center gap-2 sm:ml-3">
                <Label htmlFor={`${id}-scene-code`}>Farbcode</Label>
                <Input
                  ref={sceneInput}
                  id={`${id}-scene-code`}
                  className="w-28 font-mono"
                  value={sceneText}
                  placeholder="Markenfarbe"
                  spellCheck={false}
                  autoComplete="off"
                  aria-invalid={sceneTextInvalid || sceneTooLight}
                  aria-describedby={`${id}-scene-weak`}
                  onChange={(event) => {
                    setSceneText(event.target.value);
                    const parsed = parseSceneInput(event.target.value);
                    if (parsed !== undefined) {
                      set("scene_color", parsed);
                    }
                  }}
                />
              </span>
            </div>
            {sceneTextInvalid ? (
              <p className="mt-1 text-sm text-destructive" id={`${id}-scene-weak`} role="status">
                {sceneProblem}
              </p>
            ) : sceneTooLight ? (
              <p className="mt-1 text-sm text-destructive" id={`${id}-scene-weak`} role="status">
                Die helle Schrift am Seitenfuß ist auf dieser Farbe schlecht lesbar (Kontrast{" "}
                {formatRatio(Math.floor(sceneRatio * 10) / 10)}:1, nötig{" "}
                {formatRatio(SCENE_MIN_CONTRAST)}:1). Bitte eine dunklere Farbe wählen.
              </p>
            ) : null}
          </Advanced>
        </fieldset>
        {reset("hintergrund")}
      </div>

      {/* Consent texts, terms and imprint name the organizer whatever this says (D50). */}
      <div {...part(chosen, "veranstalter")}>
        <div className="flex items-center gap-2">
          <input
            id={`${id}-organizer`}
            type="checkbox"
            className="border border-input"
            checked={style.organizer_shown}
            onChange={(event) => {
              set("organizer_shown", event.target.checked);
            }}
          />
          <Label htmlFor={`${id}-organizer`}>Veranstalter nennen</Label>
        </div>
        {/* With a logo the header shows no name, and the switch would change nothing. */}
        {hasLogo ? null : (
          <div className="mt-3 flex items-center gap-2">
            <input
              id={`${id}-header-name`}
              type="checkbox"
              className="border border-input"
              checked={style.header_name_shown}
              onChange={(event) => {
                set("header_name_shown", event.target.checked);
              }}
            />
            <Label htmlFor={`${id}-header-name`}>Name in der Kopfzeile</Label>
          </div>
        )}
        {detail.organizer_name ? null : (
          <Link
            className="mt-3 inline-block text-sm underline underline-offset-4"
            to="../einstellungen"
          >
            Veranstalter eintragen
          </Link>
        )}
        {reset("veranstalter")}
      </div>
    </SaveForm>
  );
}

// ------------------------------------------------------------- placeholder image

interface PlaceholderImageFormProps {
  readonly detail: ActionDetail;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
  /** May take a file the security check could not clear ("Ungeprüft übernehmen"). */
  readonly mayAcceptUnscanned: boolean;
}

/**
 * The picture behind the unbooked doors of this action, also the icon in the browser tab
 * (D29). Without one the doors show the brand logo and the tab a drawn door.
 */
export function PlaceholderImageForm({
  detail,
  readOnly,
  onSaved,
  mayAcceptUnscanned,
}: PlaceholderImageFormProps): ReactElement {
  const id = useId();
  // The file the security check could not clear, kept for "Ungeprüft übernehmen".
  const [unscanned, setUnscanned] = useState<{ file: File; altText: string } | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const image = detail.placeholder_image ?? null;
  const logo = image === null ? null : { src: mediaSrc(image.path), alt: image.alt_text };

  return (
    <>
      {logo !== null ? (
        <div className="mb-4 flex items-end gap-6">
          <div className="w-32">
            <FieldGrid
              fields={[{ position: 3, presentation: present("past_open"), logo }]}
              label="Unbelegtes Türchen mit diesem Bild"
              fieldTerm="door"
              layout="grid"
              grid={{
                columns: { mobile: 1, tablet: 1, desktop: 1 },
                tile_ratio: { width: 1, height: 1 },
              }}
            />
          </div>
          <div className="text-sm">
            <img className="size-8 border border-border" src={logo.src} alt="" />
            <p className="mt-1 text-muted-foreground">im Browser-Tab</p>
          </div>
          {image === null || readOnly ? null : (
            <div className="grid gap-1">
              <CropButton
                assetId={assetIdOf(image.path)}
                alt={image.alt_text}
                label="Bild für unbelegte Türchen zuschneiden"
                onApply={async (crop) => {
                  const outcome = await uploadPlaceholderImage(detail.action_id, {
                    assetId: assetIdOf(image.path),
                    crop,
                  });
                  if (outcome.ok) {
                    onSaved();
                  }
                  return outcome;
                }}
              />
            </div>
          )}
        </div>
      ) : null}
      <SaveForm
        label="Bild für unbelegte Türchen"
        readOnly={readOnly}
        onSaved={onSaved}
        submitLabel={image === null ? "Hochladen" : "Ersetzen"}
        submit={async (form) => {
          const file = form.get("placeholder_file") as File;
          const alt = form.get("placeholder_alt");
          const altText = typeof alt === "string" ? alt : "";
          const outcome = await uploadPlaceholderImage(detail.action_id, { file, altText });
          setUnscanned(!outcome.ok && outcome.unscanned === true ? { file, altText } : null);
          setAcceptError(null);
          return outcome;
        }}
      >
        <FileDrop
          id={`${id}-file`}
          name="placeholder_file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          prompt="Bild auswählen oder hierher ziehen"
          hint="PNG, JPEG, WebP oder SVG bis 2 MB, quadratisch ab 180 px"
          required
        />
        <div className="mt-3 grid gap-1.5">
          <Label htmlFor={`${id}-alt`}>Beschreibung für Screenreader</Label>
          <Input
            id={`${id}-alt`}
            name="placeholder_alt"
            maxLength={300}
            placeholder={detail.title}
            defaultValue={image?.alt_text ?? ""}
          />
        </div>
      </SaveForm>
      {mayAcceptUnscanned && unscanned !== null && !readOnly ? (
        <>
          <UnscannedAccept
            busy={accepting}
            onAccept={(reason) => {
              setAccepting(true);
              uploadPlaceholderImage(detail.action_id, { ...unscanned, unscannedReason: reason })
                .then((outcome) => {
                  if (outcome.ok) {
                    setUnscanned(null);
                    onSaved();
                  } else {
                    setAcceptError(outcome.message);
                  }
                })
                .catch((error: unknown) => {
                  console.error("[CalendarDesign] unscanned placeholder failed", error);
                  setAcceptError("Der Schritt konnte nicht ausgeführt werden.");
                })
                .finally(() => {
                  setAccepting(false);
                });
            }}
          />
          {acceptError !== null ? (
            <Notice tone="error" className="mt-3">
              {acceptError}
            </Notice>
          ) : null}
        </>
      ) : null}
      {image !== null && !readOnly ? (
        <SaveForm
          label="Bild für unbelegte Türchen entfernen"
          readOnly={false}
          onSaved={onSaved}
          submitLabel="Bild entfernen"
          submit={() =>
            saveActionSection("placeholder-image/remove", undefined, detail.action_id)
          }
        />
      ) : null}
    </>
  );
}
